/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { organizationRemoteSessionIssuersSetEMAProviders } from "../funcs/organizationRemoteSessionIssuersSetEMAProviders.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { EMAProviderOrder } from "../models/components/emaproviderorder.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  SetOrganizationRemoteSessionIssuerEMAProvidersRequest,
  SetOrganizationRemoteSessionIssuerEMAProvidersSecurity,
} from "../models/operations/setorganizationremotesessionissueremaproviders.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type SetOrganizationRemoteSessionIssuerEMAProvidersMutationVariables = {
  request: SetOrganizationRemoteSessionIssuerEMAProvidersRequest;
  security?: SetOrganizationRemoteSessionIssuerEMAProvidersSecurity | undefined;
  options?: RequestOptions;
};

export type SetOrganizationRemoteSessionIssuerEMAProvidersMutationData =
  EMAProviderOrder;

export type SetOrganizationRemoteSessionIssuerEMAProvidersMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * setEMAProviders organizationRemoteSessionIssuers
 *
 * @remarks
 * Replace the EMA providers the vendor authorization server this issuer names accepts, the providers that may issue ID-JAGs for it. They are stored as an ordered list, but each endpoint can use only the provider its sign-in goes through: the sign-in's trusted external issuer, or the organization's Speakeasy issuer for a sign-in that trusts none. So the list acts as the set of accepted providers, and its order does not change who vouches; a list that leaves out an endpoint's provider leaves that endpoint with nobody to vouch. An empty list restores the derived default, which accepts every provider: an endpoint whose sign-in trusts an external issuer uses that issuer alone, and one that trusts none uses the organization's Speakeasy issuer when it has one. The list applies organization-wide to every issuer with the same canonical issuer URL. With expected_providers, the write happens only while the saved list still matches it. Requires org:admin and the ema-providers rollout; restoring the default only needs org:admin. Not available from support or impersonated sessions.
 */
export function useSetOrganizationRemoteSessionIssuerEMAProvidersMutation(
  options?: MutationHookOptions<
    SetOrganizationRemoteSessionIssuerEMAProvidersMutationData,
    SetOrganizationRemoteSessionIssuerEMAProvidersMutationError,
    SetOrganizationRemoteSessionIssuerEMAProvidersMutationVariables
  >,
): UseMutationResult<
  SetOrganizationRemoteSessionIssuerEMAProvidersMutationData,
  SetOrganizationRemoteSessionIssuerEMAProvidersMutationError,
  SetOrganizationRemoteSessionIssuerEMAProvidersMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildSetOrganizationRemoteSessionIssuerEMAProvidersMutation(
      client,
      options,
    ),
    ...options,
  });
}

export function mutationKeySetOrganizationRemoteSessionIssuerEMAProviders(): MutationKey {
  return [
    "@gram/client",
    "organizationRemoteSessionIssuers",
    "setEMAProviders",
  ];
}

export function buildSetOrganizationRemoteSessionIssuerEMAProvidersMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: SetOrganizationRemoteSessionIssuerEMAProvidersMutationVariables,
  ) => Promise<SetOrganizationRemoteSessionIssuerEMAProvidersMutationData>;
} {
  return {
    mutationKey: mutationKeySetOrganizationRemoteSessionIssuerEMAProviders(),
    mutationFn:
      function setOrganizationRemoteSessionIssuerEMAProvidersMutationFn({
        request,
        security,
        options,
      }): Promise<SetOrganizationRemoteSessionIssuerEMAProvidersMutationData> {
        const mergedOptions = {
          ...hookOptions,
          ...options,
          fetchOptions: {
            ...hookOptions?.fetchOptions,
            ...options?.fetchOptions,
            signal: combineSignals(
              hookOptions?.fetchOptions?.signal,
              options?.fetchOptions?.signal,
            ),
          },
        };
        return unwrapAsync(organizationRemoteSessionIssuersSetEMAProviders(
          client$,
          request,
          security,
          mergedOptions,
        ));
      },
  };
}
